import { FlightPlanRouteModel } from '@fe/core';
import { distance, lineString, nearestPointOnLine } from '@turf/turf';
import { LngLatArray, RouteProjectionMode } from '../types';

export type RouteProjection = {
    project(point: LngLatArray): number;
};

const useTurf = true;

class DistanceToOriginProjection implements RouteProjection {
    private origin: LngLatArray;
    constructor(private readonly route: FlightPlanRouteModel) {
        this.origin = route.waypoints[0].position;
    }

    project(point: LngLatArray): number {
        return distance(this.origin, point, { units: 'nauticalmiles' });
    }
}

class DistanceAlongRouteProjection implements RouteProjection {
    private distances: [number, LngLatArray][] = [];
    private line: GeoJSON.Feature<GeoJSON.LineString>;
    constructor(private readonly route: FlightPlanRouteModel) {
        this.line = lineString(removeDuplicates(route.waypoints.map((x) => x.position)));
        let previousPoint = route.waypoints[0].position;
        let cumDist = 0;
        for (const point of route.waypoints) {
            cumDist += distance(previousPoint, point.position, { units: 'nauticalmiles' });
            this.distances.push([cumDist, point.position]);
            previousPoint = point.position;
        }
    }

    project(point: LngLatArray): number {
        if (useTurf) {
            return nearestPointOnLine(this.line, point, { units: 'nauticalmiles' }).properties.location;
        }

        for (const [distance, position] of this.distances) {
            if (position[0] === point[0] && position[1] === point[1]) {
                return distance;
            }
        }

        let minDist = 9999999999;
        let projectedFrac = 0;
        let minStartWaypoint = 0;

        for (let i = 1; i < this.distances.length; i++) {
            const a = this.distances[i - 1][1];
            const b = this.distances[i][1];

            const candidate = this.projectPointOntoSegment(point, a, b);

            const dist = distance(point, candidate.point, { units: 'nauticalmiles' });

            if (dist < minDist) {
                minDist = dist;
                projectedFrac = candidate.fraction;
                minStartWaypoint = i - 1;
            }
        }

        return (
            projectedFrac *
                distance(this.distances[minStartWaypoint][1], this.distances[minStartWaypoint + 1][1], { units: 'nauticalmiles' }) +
            this.distances[minStartWaypoint][0]
        );
    }
    private projectPointOntoSegment(p: LngLatArray, a: LngLatArray, b: LngLatArray) {
        const referenceLatitude = ((p[1] + a[1] + b[1]) / 3) * (Math.PI / 180);
        const longitudeScale = Math.cos(referenceLatitude);

        const toLocal = ([longitude, latitude]: LngLatArray): [number, number] => [longitude * longitudeScale, latitude];

        const [px, py] = toLocal(p);
        const [ax, ay] = toLocal(a);
        const [bx, by] = toLocal(b);

        const segmentX = bx - ax;
        const segmentY = by - ay;
        const pointX = px - ax;
        const pointY = py - ay;
        const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;

        if (segmentLengthSquared === 0) {
            //return a;
        }

        const t = Math.max(0, Math.min(1, (pointX * segmentX + pointY * segmentY) / segmentLengthSquared));

        const projectedX = ax + t * segmentX;
        const projectedY = ay + t * segmentY;

        return { point: [projectedX / longitudeScale, projectedY], fraction: t };
    }
    private projectPointOntoSegment2(p: LngLatArray, a: LngLatArray, b: LngLatArray): LngLatArray {
        const diffX = b[0] - a[0];
        const diffY = b[1] - a[1];
        const pma = [p[0] - a[0], p[1] - a[1]];
        const bma = [b[0] - a[0], b[1] - a[1]];

        const t = Math.max(0, Math.min(1, (pma[0] * bma[0] + pma[1] * bma[1]) / (diffX * diffX + diffY * diffY)));

        return [a[0] + t * bma[0], a[1] + t * bma[1]];
    }
}

export function getProjection(type: RouteProjectionMode, route: FlightPlanRouteModel): RouteProjection {
    switch (type) {
        case RouteProjectionMode.distanceAlongRoute:
            return new DistanceAlongRouteProjection(route);
        case RouteProjectionMode.distanceToOrigin:
            return new DistanceToOriginProjection(route);
    }
}
function removeDuplicates(route: LngLatArray[]): LngLatArray[] {
    let previous = route[0];
    const result = [route[0]];
    for (let i = 1; i < route.length; i++) {
        const current = route[i];

        // If this point is the same as the previous skip it
        if (previous[0] === current[0] && previous[1] === current[1]) {
            continue;
        }
        result.push(current);
        previous = current;
    }

    return result;
}
